import React from 'react';
import Autosuggest from 'react-autosuggest';
import PropTypes from 'prop-types';
import CloseCircleFilled from '@ant-design/icons/CloseCircleFilled';
import { modes } from './constants';
import Container from './components/Container';
import AutoSelectInputWrapper from './styled/AutoSelectInputWrapper';
import AutoSelectIcon from './styled/AutoSelectIcon';
import AutoSelectOption from './styled/AutoSelectOption';
import AutoSelectDropDown from './styled/AutoSelectDropDown';
import AutoSelectSkeleton from './styled/AutoSelectSkeleton';
import Empty from './styled/Empty';
import LoaderSvg from './icons/loader.svg';
import SearchSvg from './icons/search.svg';
import SearchSvgBlue from './icons/search_blue.svg';

/*
 * Key Learnings:
 * This component supports IOS
 * Unfortunately IOS doesn't support "fixed" css containers.
 * https://stackoverflow.com/questions/29001977/safari-in-ios8-is-scrolling-screen-when-fixed-elements-get-focus
 *
 * Overflow: "hidden" is ignored by IOS
 * https://www.npmjs.com/package/body-scroll-lock
 *
 * The "Container" portal lifts the component closer to the root (body)
 * element allow us to use "absolute" positioning in favour of "fixed"
 * More on react portals - https://reactjs.org/docs/portals.html
 */

class Complete extends React.Component {
  state = {
    value: '',
    backdropActive: false,
    inputInFocus: true,
    suggestions: [],
    isEmpty: true,
  };

  get suggestions() {
    const { suggestions } = this.state;
    return suggestions;
  }

  handleChange = (event, suggestion) => {
    const { newValue } = suggestion;
    this.setState({
      value: newValue,
    });
  };

  handleFocus = () => {
    this.setState({
      backdropActive: true,
      inputInFocus: true,
    });
  };

  handleBlur = () => {
    this.setState({
      inputInFocus: false,
    });
  };

  onSuggestionsFetchRequested = (instance) => {
    const { value } = instance;
    const { filterSuggestions, suggestions } = this.props;
    const filtered = filterSuggestions(suggestions, value);
    this.setState({
      suggestions: filtered,
      isEmpty: filtered.length === 0,
    });
  };

  onSuggestionsClearRequested = () => {
    this.setState({
      suggestions: [],
      isEmpty: true,
    });
  };

  handleReset = () => {
    const { onReset } = this.props;
    const { value } = this.state;
    this.setState(
      {
        value: '',
        backdropActive: false,
      },
      () => {
        onReset(value);
      }
    );
  };

  handleSelect = (event, suggestion) => {
    const { onSelect } = this.props;
    const { value } = this.state;
    this.setState({ backdropActive: false }, () => {
      onSelect(suggestion, value);
    });
  };

  isMobileMode = () => {
    const { mode } = this.props;
    return mode === modes.MOBILE;
  };

  render() {
    const {
      placeholder,
      getSuggestionValue,
      isBusy,
      renderSectionTitle,
      renderSuggestionValue,
      getSectionSuggestions,
      onSuggestionSelected,
    } = this.props;
    const { value, backdropActive, inputInFocus, isEmpty } = this.state;

    const renderSkeleton = () => (
      <AutoSelectSkeleton>
        <LoaderSvg />
      </AutoSelectSkeleton>
    );

    const renderEmpty = () => <Empty>No results found</Empty>;

    const renderSuggestionsContainer = ({ containerProps, children }) => (
      <AutoSelectDropDown {...containerProps} isMobileMode={this.isMobileMode()}>
        {inputInFocus && isBusy && backdropActive ? renderSkeleton() : children}
        {inputInFocus && isEmpty && value && !isBusy && renderEmpty()}
      </AutoSelectDropDown>
    );

    const renderSuggestion = (suggestion) => (
      <AutoSelectOption onClick={(evt) => this.handleSelect(evt, suggestion)}>
        {renderSuggestionValue ? renderSuggestionValue(suggestion) : getSuggestionValue(suggestion)}
      </AutoSelectOption>
    );

    const renderInputComponent = (inputProps) => (
      <AutoSelectInputWrapper inFocus={inputInFocus}>
        <AutoSelectIcon type="button" isPrefix>
          {inputInFocus ? <SearchSvgBlue /> : <SearchSvg />}
        </AutoSelectIcon>
        {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
        <input {...inputProps} autoFocus={backdropActive} />
        {this.state.value ? (
          <AutoSelectIcon type="button" onMouseDown={this.handleReset} isSuffix>
            <CloseCircleFilled />
          </AutoSelectIcon>
        ) : null}
      </AutoSelectInputWrapper>
    );

    const inputProps = {
      placeholder,
      value,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      onChange: this.handleChange,
    };

    const renderAutoSelect = () => (
      <Autosuggest
        multiSection={!!(renderSectionTitle && getSectionSuggestions)}
        suggestions={this.state.suggestions}
        onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
        onSuggestionsClearRequested={this.onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        inputProps={inputProps}
        renderSuggestion={renderSuggestion}
        renderInputComponent={renderInputComponent}
        renderSuggestionsContainer={renderSuggestionsContainer}
        renderSectionTitle={renderSectionTitle}
        getSectionSuggestions={getSectionSuggestions}
        focusInputOnSuggestionClick={false}
        onSuggestionSelected={onSuggestionSelected}
      />
    );

    const renderMobile = () => (
      <Container active={backdropActive && this.isMobileMode()}>{renderAutoSelect()}</Container>
    );

    if (this.isMobileMode() && backdropActive) {
      return (
        <>
          {renderMobile()}
          {/** Fill space now we don't render the input below */}
          <div style={{ height: 56 }} />
        </>
      );
    }

    return renderAutoSelect();
  }
}

Complete.propTypes = {
  mode: PropTypes.string,
  placeholder: PropTypes.string,
  suggestions: PropTypes.array,
  filterSuggestions: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
  onReset: PropTypes.func,
  renderSectionTitle: PropTypes.func,
  getSectionSuggestions: PropTypes.func,
  getSuggestionValue: PropTypes.func,
  renderSuggestionValue: PropTypes.func,
  isBusy: PropTypes.bool,
  onSuggestionSelected: PropTypes.func,
};

Complete.defaultProps = {
  mode: modes.DESKTOP,
  placeholder: '',
  suggestions: [],
  isBusy: false,
  onSelect: (value) => {
    console.log('selected', value);
  },
  renderSectionTitle: null,
  getSectionSuggestions: null,
  onReset: (value) => value,
  getSuggestionValue: (value) => value,
  renderSuggestionValue: null,
  onSuggestionSelected: (value) => value,
};

export default Complete;

